import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Spinner, Txt } from '@vst/beam';

import { useScreenResolution } from '@app/hooks';
import {
  getEicMessage,
  selectAddOns,
  setDisclosureChecked,
  setEicMessageError,
  setErrorDisclosureCheck,
  setScrollToConsent,
} from '@app/redux/addOns';
import { getAllDetails, selectStreamOnPromo, setConfigureStream, setConfirmModal } from '@app/redux/streamOnPromo';

import {
  Banner,
  Cart,
  ConfirmAddressModal,
  ErrorToast,
  ModalContainer,
  Page,
  ShippingAddress,
  Sms,
} from '../../components';
import { ConsentSection } from '../../components/ConsentSection';
import TermsAndConditionsModal from '../../components/TermsAndConditionsModal';
import { ErrorPage } from '../error';
import { useTranslation } from '../../utils';

import styles from './styles.module.scss';

const LoadingModal = ({ t }: { t: (key: string) => string }) => (
  <ModalContainer id="Address">
    <div className={styles['scrubSpinnerContainer']}>
      <Spinner aria-label="Loading Spinner" />
      <Txt color="subtle" variant="bodySmallRegular">
        {t('Loading')}
      </Txt>
    </div>
  </ModalContainer>
);

const AddressModal = ({ handleConfirm, onClose }: { handleConfirm: boolean; onClose: () => void }) => (
  <ModalContainer id="Address">
    <ConfirmAddressModal onClose={onClose} handleConfirm={handleConfirm} />
  </ModalContainer>
);

const TermsModal = ({ onClose }: { onClose: () => void }) => (
  <ModalContainer id="Terms">
    <TermsAndConditionsModal setShowTermsModal={onClose} />
  </ModalContainer>
);

export const OrderReview = (): JSX.Element => {
  const dispatch = useDispatch();
  const {
    isOrderSubmitting,
    isScrubbingLoading,
    confirmModal,
    offerDetails,
    didOrderFail,
    configureStreamSuccess,
    configureStreamLoading,
    configureStreamError,
  } = useSelector(selectStreamOnPromo);

  const { shouldDisplaySms, offerId, productTypeId, eicRequired, loading } = offerDetails;

  const { t } = useTranslation(['StreamOnPromo:Shipping.AddressScrub', 'StreamOnPromo:Disclosure']);
  const [showTermsModal, setShowTermsModal] = useState(false);

  const { scrollToConsent, eicMessageLoading, getEicMessageError, eicMessageSuccess } = useSelector(selectAddOns);

  const consentRef = useRef<HTMLInputElement>(null);
  const { isMedium, isSmall, isExtraSmall } = useScreenResolution();
  const isMediumOrLess = isMedium || isSmall || isExtraSmall;

  useEffect(() => {
    if (!productTypeId) {
      dispatch(getAllDetails());
      return;
    }

    if (eicRequired && offerId && productTypeId && !configureStreamSuccess) {
      dispatch(
        setConfigureStream({
          shoppingCartId: offerId,
          rootProductTypeId: productTypeId,
          productTypeId,
        })
      );
    }

    return () => {
      dispatch(setDisclosureChecked(false));
      dispatch(setErrorDisclosureCheck(false));
      dispatch(setEicMessageError(null));
    };
  }, [productTypeId, offerId, eicRequired, configureStreamSuccess]);

  useEffect(() => {
    if (configureStreamSuccess && !eicMessageSuccess) {
      dispatch(getEicMessage({ shoppingCartId: offerId }));
    }
  }, [dispatch, configureStreamSuccess, eicMessageSuccess]);

  useEffect(() => {
    if (scrollToConsent && consentRef.current) {
      consentRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      dispatch(setScrollToConsent(false));
    }
  }, [dispatch, scrollToConsent]);

  if (loading) {
    <div className={styles['spinnerContainer']}>
      <Spinner />
    </div>;
  }

  if (configureStreamError || getEicMessageError) {
    return <ErrorPage />;
  }

  return (
    <Page>
      {confirmModal && !isScrubbingLoading && (
        <AddressModal handleConfirm={confirmModal} onClose={() => dispatch(setConfirmModal(false))} />
      )}
      {isScrubbingLoading && <LoadingModal t={t} />}
      {didOrderFail.didOrderFail && !isOrderSubmitting && <ErrorToast />}
      {showTermsModal && <TermsModal onClose={() => setShowTermsModal(false)} />}

      <div className={styles['bannerContainer']}>
        <Banner />
      </div>

      <div className={styles['pageLayout']}>
        <div
          className={styles['pageContent']}
          style={
            isMediumOrLess ? { display: 'flex', flexDirection: 'column' } : { display: 'flex', flexDirection: 'row' }
          }
        >
          <div className={styles['container']} data-cy="shippingAddress">
            <ShippingAddress />
            {shouldDisplaySms && <Sms />}
            {eicRequired && (
              <div className={styles['consentContainer']}>
                {eicMessageLoading || configureStreamLoading ? (
                  <div className={styles['loadingContainer']}>
                    <div className={styles['largeDetail']} />
                    <div className={styles['smallDetail']} />
                  </div>
                ) : (
                  <ConsentSection consentRef={consentRef} t={t} />
                )}
              </div>
            )}
          </div>

          <Cart setShowTermsModal={setShowTermsModal} />
        </div>
      </div>
    </Page>
  );
};
